import React, { Fragment, useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import { splitNumberDigits } from 'helpers/math'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import THead from 'components/ContentBox/Global/ReportTable/THead'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import { ExcelRowsParams, setCellParams } from 'components/utils/ExcelExport'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'
import { observer } from 'mobx-react'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'
import { useHistory } from 'react-router-dom'

const ProfitabilityPrograms = () => {
  const [catalog, setCatalog] = useState({})
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  const {
    refWrapper,
    refSticky,
    refThead,
    data,
    setData,
    isLoading,
    setIsLoading,
    getTdClassNames,
  } = useTableFixedHeader()

  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'ebooks_books',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      company: 'Company',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start date',
      end_date: 'End date',
    },
  })

  const filterCnf = [
    {
      type: 'select-multi',
      label: 'Локация',
      nameState: 'location',
      placeholder: 'Локация',
      values:
        catalog.locations?.sort((a, b) => (a.name > b.name ? 1 : -1)) || [],
      option_value_key: 'id',
      option_label_key: 'name',
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from', 'date_until'],
      label: 'Период',
    },
    {
      type: 'select-multi',
      label: 'Тип программы',
      placeholder: 'Тип программы',
      values: catalog.program_types || [],
      sort_key: 'name',
      nameState: 'program_type',
      is_search: true,
    },
    {
      type: 'type_selector',
      label: 'Формат',
      values: [
        { id: 2, name: 'Все группы' },
        { id: 1, name: 'Онлайн' },
        { id: 0, name: 'Офлайн' },
      ],
      nameState: 'online',
    },
  ]

  const cfg = {
    cols: [
      {
        name: 'Школа',
        group: true,
        formatter: (row) =>
          catalog.locations?.find((l) => l.id === row.id)?.name || row.id,
        classes: [st.color, st.fw500],
      },
      {
        name: 'Тип программы',
        formatter: (row) => {
          const foundProgramType = catalog.program_types?.find(
            (skl) => skl.id === row.program_type,
          )
          return foundProgramType?.name || row.program_type
        },
        classes: [st.color, st.fz12, st.fw500],
      },
      {
        name: 'Количество отработанных часов',
        formatter: (row) => splitNumberDigits(row.student.man_hours.toFixed(2)),
        classes: [st.fz12, st.textAlignCenter],
      },
      {
        name: 'Наполняемость',
        formatter: (row) => {
          const value = row?.student?.count_students_per_group

          if (value === undefined || value === null || isNaN(Number(value))) {
            return splitNumberDigits('0.00')
          }

          return splitNumberDigits(Number(value).toFixed(2))
        },
        classes: [st.fz12, st.textAlignCenter],
      },
      {
        name: 'Общая сумма',
        formatter: (row) =>
          splitNumberDigits((row.student.income / 100).toFixed(2)),
        classes: [st.fz12, st.textAlignCenter],
      },
      {
        name: 'Средний чек',
        formatter: (row) =>
          splitNumberDigits(
            (row.student.avg_income_per_person / 100).toFixed(2),
          ),
        classes: [st.fz12, st.textAlignCenter],
      },
      {
        name: 'Выручка с 1 ак. ч.',
        formatter: (row) =>
          splitNumberDigits(row.student.income_per_hour.toFixed(2)),
        classes: [st.fz12, st.textAlignCenter],
      },
      {
        name: 'Расход на учителя',
        formatter: (row) =>
          splitNumberDigits((row.teacher.expense / 100).toFixed(2)),
        classes: [st.fz12, st.textAlignCenter],
      },
      {
        name: 'Средняя себестоимость 1 ак. ч.',
        formatter: (row) =>
          splitNumberDigits(
            row.student.man_hours_income_expense_ratio.toFixed(2),
          ),
        classes: [st.fz12, st.textAlignCenter],
      },
      {
        name: 'Рентабельность 1 ак. ч.',
        formatter: (row) =>
          splitNumberDigits(row.student.profitability_per_hour.toFixed(2)),
        classes: [st.fz12, st.textAlignCenter],
      },
      {
        name: 'Прибыль 1 ак. ч.',
        formatter: (row) =>
          splitNumberDigits((row.student.profit_per_hour / 100).toFixed(2)),
        classes: [st.fz12, st.textAlignCenter],
      },
    ],
  }

  // Функция для безопасного получения значения с проверкой как в cfg
  const safeFormatter = (
    row,
    fieldPath,
    divisionBy100 = false,
    decimals = 2,
  ) => {
    const getValue = (obj, path) => {
      return path.split('.').reduce((acc, key) => {
        if (acc && acc[key] !== undefined && acc[key] !== null) {
          return acc[key]
        }
        return undefined
      }, obj)
    }

    let value = getValue(row, fieldPath)

    if (value === undefined || value === null || isNaN(Number(value))) {
      return '0.00'
    }

    let numValue = Number(value)
    if (divisionBy100) {
      numValue = numValue / 100
    }

    return splitNumberDigits(numValue.toFixed(decimals))
  }

  useEffect(() => {
    setIsLoading(true)
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/profitability_programs',
    })
      .then(({ data }) => {
        setIsLoading(false)
        setCatalog(data)

        const filteredLocations = data.locations?.filter(
          (location) => location.program_types,
        )
        setData({ locations: filteredLocations })
      })
      .catch(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    if (filter && Object.keys(filter).length === 0) {
      return
    }
    setIsLoading(true)
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/profitability_programs',
      params: {
        ...filter,
      },
    })
      .then(({ data }) => {
        const getDataWithCorrectProgramTypes = () => {
          const filteredLocations = data.result.locations.filter(
            (location) => location.program_types,
          )

          if (filter.program_type) {
            const programTypeId = parseInt(filter.program_type, 10)
            return filteredLocations
              .map((location) => {
                const matchingProgramType = location.program_types.find(
                  (pt) => pt.program_type === programTypeId,
                )
                return matchingProgramType
                  ? { ...location, program_types: [matchingProgramType] }
                  : null
              })
              .filter(Boolean)
          }

          return filteredLocations
        }

        setData({
          locations: getDataWithCorrectProgramTypes(),
          total: data?.result?.total,
        })
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [filter])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/profitability-programs') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const downloadExcel = (data) => {
    import('xlsx-js-style').then((XLSX) => {
      const headRows = [cfg.cols.map((col) => ({ v: col.name }))]

      headRows[0] = setCellParams(headRows[0], ExcelRowsParams.headerRowParam)

      let tableData = []

      if (filter && Object.keys(filter).length > 0 && !filter.location) {
        const totalStudentsPerGroup =
          data?.locations?.reduce((totalSum, location) => {
            const locationSum = location.program_types.reduce(
              (programSum, program) =>
                programSum + (program.student.count_students_per_group || 0),
              0,
            )
            return totalSum + locationSum
          }, 0) || 0

        const totalIncomePerHour =
          data?.locations?.reduce((totalSum, location) => {
            const locationSum = location.program_types.reduce(
              (programSum, program) =>
                programSum + (program.student.income_per_hour || 0),
              0,
            )
            return totalSum + locationSum
          }, 0) || 0

        const totalProfitabilityPerHour =
          data?.locations?.reduce((totalSum, location) => {
            const locationSum = location.program_types.reduce(
              (programSum, program) =>
                programSum + (program.student.profitability_per_hour || 0),
              0,
            )
            return totalSum + locationSum
          }, 0) || 0

        const totalProfitPerHour =
          data?.locations?.reduce((totalSum, location) => {
            const locationSum = location.program_types.reduce(
              (programSum, program) =>
                programSum + (program.student.profit_per_hour || 0),
              0,
            )
            return totalSum + locationSum
          }, 0) || 0

        const totalAvgIncomePerPerson =
          data?.locations?.reduce((totalSum, location) => {
            const locationSum = location.program_types.reduce(
              (programSum, program) =>
                programSum + (program.student.avg_income_per_person || 0),
              0,
            )
            return totalSum + locationSum
          }, 0) || 0

        const totalRow = [
          {
            v: 'Итого',
            ...ExcelRowsParams.footerRowParams.firstCell,
          },
          { v: '' },
          {
            v: data?.total?.student?.man_hours
              ? splitNumberDigits(data.total.student.man_hours.toFixed(2))
              : '0.00',
          },
          {
            v: splitNumberDigits(totalStudentsPerGroup.toFixed(2)),
          },
          {
            v: data?.total?.student?.income
              ? splitNumberDigits((data.total.student.income / 100).toFixed(2))
              : '0.00',
          },
          {
            v: splitNumberDigits((totalAvgIncomePerPerson / 100).toFixed(2)),
          },
          {
            v: splitNumberDigits(totalIncomePerHour.toFixed(2)),
          },
          {
            v: data?.total?.teacher?.expense
              ? splitNumberDigits((data.total.teacher.expense / 100).toFixed(2))
              : '0.00',
          },
          {
            v: data?.total?.student?.man_hours_income_expense_ratio
              ? splitNumberDigits(
                  data.total.student.man_hours_income_expense_ratio.toFixed(2),
                )
              : '0.00',
          },
          {
            v: splitNumberDigits(totalProfitabilityPerHour.toFixed(2)),
          },
          {
            v: splitNumberDigits((totalProfitPerHour / 100).toFixed(2)),
          },
        ]

        const formattedTotalRow = totalRow.map((item, id) =>
          id === 0
            ? item
            : { ...item, ...ExcelRowsParams.footerRowParams.cell },
        )
        tableData.push(formattedTotalRow)
      }

      let merges = []
      let wscols = [
        { wch: 20 },
        { wch: 20 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 25 },
        { wch: 20 },
        { wch: 30 },
        { wch: 25 },
        { wch: 20 },
      ]
      let rows = [{ hpx: 40 }]

      let currentRow = 1

      data?.locations?.forEach((location) => {
        if (location.program_types.length > 1) {
          merges.push({
            s: { r: currentRow, c: 0 },
            e: { r: currentRow + location.program_types.length - 1, c: 0 },
          })
        }

        location.program_types.forEach((program_type) => {
          let rowData = [
            {
              v:
                catalog.locations?.find((el) => el.id === location.id)?.name ||
                location.id,
            },
            {
              v:
                catalog.program_types?.find(
                  (el) => el.id === program_type.program_type,
                )?.name || program_type.program_type,
            },
            {
              v: splitNumberDigits(program_type.student.man_hours.toFixed(2)),
            },
            {
              v: safeFormatter(
                program_type,
                'student.count_students_per_group',
                false,
                2,
              ),
            },
            {
              v: splitNumberDigits(
                (program_type.student.income / 100).toFixed(2),
              ),
            },
            {
              v: splitNumberDigits(
                (program_type.student.avg_income_per_person / 100).toFixed(2),
              ),
            },
            {
              v: splitNumberDigits(
                program_type.student.income_per_hour.toFixed(2),
              ),
            },
            {
              v: splitNumberDigits(
                (program_type.teacher.expense / 100).toFixed(2),
              ),
            },
            {
              v: splitNumberDigits(
                program_type.student.man_hours_income_expense_ratio.toFixed(2),
              ),
            },
            {
              v: splitNumberDigits(
                program_type.student.profitability_per_hour.toFixed(2),
              ),
            },
            {
              v: splitNumberDigits(
                (program_type.student.profit_per_hour / 100).toFixed(2),
              ),
            },
          ]
          rowData = setCellParams(rowData, ExcelRowsParams.tableRowParam.cell)
          tableData.push(rowData)
          currentRow++
        })

        currentRow++

        const locationStudentsPerGroup = location.program_types.reduce(
          (sum, program) =>
            sum + (program.student.count_students_per_group || 0),
          0,
        )

        const locationIncomePerHour = location.program_types.reduce(
          (sum, program) => sum + (program.student.income_per_hour || 0),
          0,
        )

        const locationProfitabilityPerHour = location.program_types.reduce(
          (sum, program) => sum + (program.student.profitability_per_hour || 0),
          0,
        )

        const locationProfitPerHour = location.program_types.reduce(
          (sum, program) => sum + (program.student.profit_per_hour || 0),
          0,
        )

        const locationAvgIncomePerPerson = location.program_types.reduce(
          (sum, program) => sum + (program.student.avg_income_per_person || 0),
          0,
        )

        let footerRow = [
          {
            v: 'Итого',
            ...ExcelRowsParams.footerRowParams.firstCell,
          },
          { v: '' },
          {
            v: splitNumberDigits(location.total.student.man_hours.toFixed(2)),
          },
          {
            v: splitNumberDigits(locationStudentsPerGroup.toFixed(2)),
          },
          {
            v: splitNumberDigits(
              (location.total.student.income / 100).toFixed(2),
            ),
          },
          {
            v: splitNumberDigits((locationAvgIncomePerPerson / 100).toFixed(2)),
          },
          {
            v: splitNumberDigits(locationIncomePerHour.toFixed(2)),
          },
          {
            v: splitNumberDigits(
              (location.total.teacher.expense / 100).toFixed(2),
            ),
          },
          {
            v: splitNumberDigits(
              location.total.student.man_hours_income_expense_ratio.toFixed(2),
            ),
          },
          {
            v: splitNumberDigits(locationProfitabilityPerHour.toFixed(2)),
          },
          {
            v: splitNumberDigits((locationProfitPerHour / 100).toFixed(2)),
          },
        ]
        footerRow = footerRow.map((item, id) =>
          id === 0
            ? item
            : { ...item, ...ExcelRowsParams.footerRowParams.cell },
        )
        tableData.push(footerRow)
        currentRow++
      })

      let wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([...headRows, ...tableData])
      ws['!cols'] = wscols
      ws['!merges'] = merges
      ws['!rows'] = rows

      XLSX.utils.book_append_sheet(wb, ws, 'List 1')
      XLSX.writeFile(wb, 'Рентабельность программ.xlsx')
    })
  }

  const locations = data?.locations?.filter((e) => e.program_types) || []
  const isFilterEmpty = !filterTextValues.length

  return (
    <>
      <ContentHeader title='Рентабельность программ' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder={`Search...`}
          value={filter?.search || ''}
          isFilterEmpty={isFilterEmpty}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCnf}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : data ? (
        <>
          <div style={{ padding: '10px', marginBottom: '10px' }}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel} />
          </div>
          <div className={st.tableWrapper} ref={refWrapper}>
            <div ref={refSticky} className={st.sticky}>
              <table className={st.table}>
                <THead cols={cfg.cols} />
              </table>
            </div>
            <table className={st.table}>
              <THead cols={cfg.cols} ref={refThead} />
              <tbody className={`${st.tbody} ${st.tbodyStriped}`}>
                {/* Итого по фильтрам */}
                {filter &&
                  Object.keys(filter).length > 0 &&
                  !filter.location && (
                    <tr>
                      <td className={st.tdTotal}>Итого</td>
                      <td className={st.tdTotal} />
                      <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                        {splitNumberDigits(
                          data?.total?.student?.man_hours.toFixed(2),
                        )}
                      </td>
                      {/* Наполняемость - сумма по всем программам всех локаций */}
                      <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                        {splitNumberDigits(
                          data?.locations
                            ?.reduce((totalSum, location) => {
                              const locationSum = location.program_types.reduce(
                                (programSum, program) =>
                                  programSum +
                                  (program.student.count_students_per_group ||
                                    0),
                                0,
                              )
                              return totalSum + locationSum
                            }, 0)
                            .toFixed(2),
                        )}
                      </td>
                      <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                        {splitNumberDigits(
                          (data?.total?.student?.income / 100).toFixed(2),
                        )}
                      </td>
                      <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                        {splitNumberDigits(
                          (
                            data?.locations?.reduce((totalSum, location) => {
                              const locationSum = location.program_types.reduce(
                                (programSum, program) =>
                                  programSum +
                                  (program.student.avg_income_per_person || 0),
                                0,
                              )
                              return totalSum + locationSum
                            }, 0) / 100
                          ).toFixed(2),
                        )}
                      </td>
                      {/* Выручка с 1 ак. ч. - сумма по всем программам */}
                      <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                        {splitNumberDigits(
                          data?.locations
                            ?.reduce((totalSum, location) => {
                              const locationSum = location.program_types.reduce(
                                (programSum, program) =>
                                  programSum +
                                  (program.student.income_per_hour || 0),
                                0,
                              )
                              return totalSum + locationSum
                            }, 0)
                            .toFixed(2),
                        )}
                      </td>
                      <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                        {splitNumberDigits(
                          (data?.total?.teacher?.expense / 100).toFixed(2),
                        )}
                      </td>
                      <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                        {splitNumberDigits(
                          data?.total?.student?.man_hours_income_expense_ratio.toFixed(
                            2,
                          ),
                        )}
                      </td>
                      {/* Рентабельность 1 ак. ч. - сумма по всем программам */}
                      <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                        {splitNumberDigits(
                          data?.locations
                            ?.reduce((totalSum, location) => {
                              const locationSum = location.program_types.reduce(
                                (programSum, program) =>
                                  programSum +
                                  (program.student.profitability_per_hour || 0),
                                0,
                              )
                              return totalSum + locationSum
                            }, 0)
                            .toFixed(2),
                        )}
                      </td>
                      {/* Прибыль 1 ак. ч. - сумма по всем программам, деленная на 100 */}
                      <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                        {splitNumberDigits(
                          (
                            data?.locations?.reduce((totalSum, location) => {
                              const locationSum = location.program_types.reduce(
                                (programSum, program) =>
                                  programSum +
                                  (program.student.profit_per_hour || 0),
                                0,
                              )
                              return totalSum + locationSum
                            }, 0) / 100
                          ).toFixed(2),
                        )}
                      </td>
                    </tr>
                  )}

                {/* Данные по локациям */}
                {Array.isArray(data.locations) &&
                  locations.map((location) => (
                    <Fragment key={location.id}>
                      {location.program_types.map((pt, index) => (
                        <tr
                          key={`${location.id}-${index}`}
                          className={index === 0 ? st.bdt : undefined}
                        >
                          {cfg.cols.map((col, idxCol) => {
                            if (col.group) {
                              if (index === 0)
                                return (
                                  <td
                                    key={idxCol}
                                    rowSpan={location.program_types.length}
                                    className={getTdClassNames(col)}
                                  >
                                    {typeof col.formatter === 'function'
                                      ? col.formatter(location)
                                      : location[col.dataField]}
                                  </td>
                                )
                              else return null
                            } else {
                              return (
                                <td
                                  key={idxCol}
                                  className={getTdClassNames(col)}
                                >
                                  {typeof col.formatter === 'function'
                                    ? col.formatter(pt)
                                    : pt[col.dataField]}
                                </td>
                              )
                            }
                          })}
                        </tr>
                      ))}
                      {/* Итого по локации */}
                      <tr>
                        <td className={st.tdTotal}>Итого</td>
                        <td className={st.tdTotal} />
                        <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                          {splitNumberDigits(
                            location.total.student.man_hours.toFixed(2),
                          )}
                        </td>
                        <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                          {splitNumberDigits(
                            location.program_types
                              .reduce(
                                (sum, program) =>
                                  sum +
                                  (program.student.count_students_per_group ||
                                    0),
                                0,
                              )
                              .toFixed(2),
                          )}
                        </td>
                        <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                          {splitNumberDigits(
                            (location.total.student.income / 100).toFixed(2),
                          )}
                        </td>
                        <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                          {splitNumberDigits(
                            (
                              location.program_types.reduce(
                                (sum, program) =>
                                  sum +
                                  (program.student.avg_income_per_person || 0),
                                0,
                              ) / 100
                            ).toFixed(2),
                          )}
                        </td>
                        <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                          {splitNumberDigits(
                            location.program_types
                              .reduce(
                                (sum, program) =>
                                  sum + (program.student.income_per_hour || 0),
                                0,
                              )
                              .toFixed(2),
                          )}
                        </td>
                        <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                          {splitNumberDigits(
                            (location.total.teacher.expense / 100).toFixed(2),
                          )}
                        </td>
                        <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                          {splitNumberDigits(
                            location.total.student.man_hours_income_expense_ratio.toFixed(
                              2,
                            ),
                          )}
                        </td>
                        <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                          {splitNumberDigits(
                            location.program_types
                              .reduce(
                                (sum, program) =>
                                  sum +
                                  (program.student.profitability_per_hour || 0),
                                0,
                              )
                              .toFixed(2),
                          )}
                        </td>
                        {/* Прибыль 1 ак. ч. - сумма по всем программам, деленная на 100 */}
                        <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                          {splitNumberDigits(
                            (
                              location.program_types.reduce(
                                (sum, program) =>
                                  sum + (program.student.profit_per_hour || 0),
                                0,
                              ) / 100
                            ).toFixed(2),
                          )}
                        </td>
                      </tr>
                    </Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для отображения!</h1>
      )}
    </>
  )
}

export default observer(ProfitabilityPrograms)
